import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { extendObjectWithId } from 'src/shared/functions/extendObjectWithId';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { useScrollIntoView } from 'src/shared/hooks/useScrollIntoView';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import { Box } from '@mui/system';
import { getAllInvoiceArticleCategory } from 'src/shared/services/invoiceArticleCategoryService';
import { PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { InvoiceArticleCategory } from '../models/invoiceArticleCategory';
import { ArticleCategorySearchFilter } from '../models/articleCategorySearchFilter';

type InvoiceArticleCategoryDataGridProps = {
  refetchDataGrid: boolean;
  setRefetchDataGrid: (refetch: boolean) => void;
  setArticleCategoryId: (articleCategoryId: number) => void;
  setOpenEditCategory: (openEditCategory: boolean) => void;
  searchFilter: ArticleCategorySearchFilter;
  searchFilterCallback: (searchFilter: ArticleCategorySearchFilter) => void;
};

export const InvoiceArticleCategoryDataGrid = (props: InvoiceArticleCategoryDataGridProps) => {
  const { refetchDataGrid, setRefetchDataGrid, setArticleCategoryId, setOpenEditCategory, searchFilter, searchFilterCallback } = props;
  const { t } = useTranslation(['member', 'invoice', 'common']);
  const isMounted = useIsMounted();
  const { scrollIntoView, scrollRef } = useScrollIntoView();

  const [rows, setRows] = useState<InvoiceArticleCategory[]>([]);
  const [data, setData] = useState<DataGridModel>({
    loading: true,
    totalRows: 0,
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: searchFilter.pageSize,
    page: searchFilter.pageIndex,
  });

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  useEffect(() => {
    setData((prev) => ({ ...prev, ...{ page: searchFilter.pageIndex, pageSize: searchFilter.pageSize } }));
  }, [searchFilter]);

  const onPageChange = (newPageIndex: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: newPageIndex });
    scrollIntoView();
  };

  const onPageSizeChange = (newPageSize: number) => {
    searchFilterCallback({ ...searchFilter, pageIndex: 0, pageSize: newPageSize });
    scrollIntoView();
  };

  const fetchData = useCallback(async () => {
    if (isMounted()) {
      updateData('loading', true);
    }
    try {
      const result = await getAllInvoiceArticleCategory(searchFilter);
      const invoiceArticleCategoryResult = extendObjectWithId(result.data.results, 'articleCategory_id');
      if (isMounted()) {
        updateData('totalRows', result.data.totalItems);

        setTimeout(() => {
          setRows(invoiceArticleCategoryResult);
        }, 100);
      }
    } catch (e) {
      if (isMounted()) {
        ConsoleHelper.log(e);
      }
    } finally {
      if (isMounted()) {
        setRefetchDataGrid(false);
        updateData('loading', false);
      }
    }
  }, [isMounted, searchFilter, setRefetchDataGrid]);

  useEffect(() => {
    if (isMounted()) {
      fetchData();
    }
  }, [fetchData, isMounted]);

  useEffect(() => {
    if (refetchDataGrid) {
      fetchData();
    }
  }, [fetchData, refetchDataGrid]);

  const onRowClick = (params) => {
    if (params.row && params.row.articleCategory_id) {
      setArticleCategoryId(params.row.articleCategory_id);
      setOpenEditCategory(true);
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'articleCategory',
      headerName: t('invoice:articleCategoryPage.category'),
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.articleCategory || ''}</>;
      },
    },
  ];

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
      ref={scrollRef}
    >
      <DataGrid<InvoiceArticleCategory>
        autoHeight
        columns={columns}
        rows={rows}
        onRowClick={onRowClick}
        dataModel={data}
        checkboxSelection={false}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
        onSortModelChange={(newSortModel) => {
          if (newSortModel.length > 0) {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: newSortModel[0].field,
              sortDir: newSortModel[0].sort,
            });
          } else {
            searchFilterCallback({
              ...searchFilter,
              pageIndex: 0,
              sortCol: searchFilter.sortCol,
              sortDir: searchFilter.sortDir === 'asc' ? 'desc' : 'asc',
            });
          }
        }}
        selectionModel={[]}
        sortModel={[{ field: searchFilter.sortCol, sort: searchFilter.sortDir }]}
        onSelectionModelChange={() => ConsoleHelper.log('')}
      />
    </Box>
  );
};
